import { useState, useEffect, useCallback } from 'react';
import { dashboardApi, DashboardSummaryDto } from '@/services/api';
import { useDataMode } from '@/contexts/DataModeContext';
import { useCompany } from '@/contexts/CompanyContext';

interface UseDashboardDataResult {
  data: DashboardSummaryDto | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDashboardData(): UseDashboardDataResult {
  const { isLive } = useDataMode();
  const { currentCompany } = useCompany();
  const [data, setData] = useState<DashboardSummaryDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!isLive('overview')) {
      setData(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await dashboardApi.getSummary(currentCompany?.id || undefined);
      setData(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(message);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [isLive, currentCompany?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
